import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';
import { Form } from '@edx/paragon';

import messages from './City.messages';

// Components
import FormControls from './elements/FormControls';
import EditableItemHeader from './elements/EditableItemHeader';
import EmptyContent from './elements/EmptyContent';
import SwitchContent from './elements/SwitchContent';

// Selectors
import { citySelector } from '../data/selectors';

const City = ({
  formId,
  city,
  visibilityCity,
  saveState,
  error,
  intl,
  changeHandler,
  submitHandler,
  closeHandler,
  openHandler,
  changeData,
  saveData,
  onSaveComplete,
  editMode: initialEditMode,
}) => {
  const [editMode, setEditMode] = useState(changeData ? 'editing' : initialEditMode);
  useEffect(() => {
    if (changeData) {
      setEditMode('editing');
    } else {
      handleClose();
    }
  }, [changeData]);

  useEffect(() => {
    if (saveData) {
      handleSubmit();
    }
  }, [saveData]);

  const handleChange = e => {
    const { name, value } = e.target;
    changeHandler(name, value);
  };

  const handleSubmit = e => {
    // Проверяем, есть ли объект события
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    submitHandler(formId);
    if (onSaveComplete) {
      onSaveComplete(); // Сбрасываем состояние saveData в родительском компоненте
    }
  };

  const handleClose = () => {
    closeHandler(formId);
    setEditMode('static');
  };

  const handleOpen = () => {
    openHandler(formId);
    setEditMode('editing');
  };

  return (
    <SwitchContent
      className="tw-mb-10"
      expression={editMode}
      cases={{
        editing: (
          <div role="dialog" aria-labelledby={`${formId}-label`}>
            <form onSubmit={handleSubmit}>
              <Form.Group controlId={formId} isInvalid={error !== null} className="tw-mb-0">
                <label
                  className="tw-block tw-text-[16px] tw-text-neutral-1000 tw-font-medium"
                  htmlFor={formId}
                >
                  {intl.formatMessage(messages['profile.city.label'])}
                </label>
                <p className="tw-text-[14px] tw-text-placeholder tw-font-medium !tw-mb-[14px] tw-leading-[120%]">
                  {intl.formatMessage(messages['profile.city.placeholder'])}
                </p>
                <input
                  data-hj-suppress
                  className="input-text tw-pl-4 tw-w-full"
                  type="text"
                  id={formId}
                  name={formId}
                  value={city || ''}
                  onChange={handleChange}
                  placeholder={intl.formatMessage(messages['profile.city.input.placeholder'])}
                />
                {error !== null && (
                  <Form.Control.Feedback hasIcon={false}>{error}</Form.Control.Feedback>
                )}
              </Form.Group>
              <div className="tw-hidden">
                <FormControls
                  visibilityId="visibilityCity"
                  saveState={saveState}
                  visibility={visibilityCity}
                  cancelHandler={handleClose}
                  changeHandler={handleChange}
                  saveData={saveData} // Передаем состояние saveData в FormControls
                />
              </div>
            </form>
          </div>
        ),
        editable: (
          <>
            <EditableItemHeader
              content={intl.formatMessage(messages['profile.city.label'])}
              showEditButton
              onClickEdit={handleOpen}
              showVisibility={visibilityCity !== null}
              visibility={visibilityCity}
            />
            <p data-hj-suppress className="h5">
              {city}
            </p>
          </>
        ),
        empty: (
          <>
            <EditableItemHeader content={intl.formatMessage(messages['profile.city.label'])} />
            <EmptyContent onClick={handleOpen}>
              {intl.formatMessage(messages['profile.city.empty'])}
            </EmptyContent>
          </>
        ),
        static: (
          <>
            <EditableItemHeader content={intl.formatMessage(messages['profile.city.label'])} />
            <p
              data-hj-suppress
              className="tw-text-[16px] tw-text-neutral-1000 tw-font-medium tw-leading-[150%]"
            >
              {city || (
                <span className="tw-text-[14px] tw-text-placeholder tw-font-medium">
                  {intl.formatMessage(messages['profile.city.placeholder'])}
                </span>
              )}
            </p>
          </>
        ),
      }}
    />
  );
};

City.propTypes = {
  formId: PropTypes.string.isRequired,
  city: PropTypes.string,
  visibilityCity: PropTypes.oneOf(['private', 'all_users']),
  editMode: PropTypes.oneOf(['editing', 'editable', 'empty', 'static']),
  saveState: PropTypes.string,
  error: PropTypes.string,
  changeHandler: PropTypes.func.isRequired,
  submitHandler: PropTypes.func.isRequired,
  closeHandler: PropTypes.func.isRequired,
  openHandler: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
  changeData: PropTypes.bool,
  saveData: PropTypes.bool,
  onSaveComplete: PropTypes.func,
};

City.defaultProps = {
  editMode: 'static',
  saveState: null,
  city: null,
  visibilityCity: 'private',
  error: null,
  changeData: false,
  saveData: false,
  onSaveComplete: null,
};

export default connect(citySelector, {})(injectIntl(City));
